'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import type { User } from '@supabase/supabase-js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  AlertTriangle,
  MessageCircle,
  UserIcon,
  Shield,
  Send,
  ArrowLeft,
  CheckCircle,
  FileText,
  Loader2,
} from 'lucide-react';
import PageHero from '@/components/layout/PageHero';
import { createClient } from '@/lib/supabase/browser';

const ISSUE_TYPES = [
  { value: 'bug', label: 'Bug or technical issue' },
  { value: 'account', label: 'Account problem' },
  { value: 'content', label: 'Inappropriate content' },
  { value: 'job', label: 'Problem with a job posting' },
  { value: 'payment', label: 'Billing or payment issue' },
  { value: 'feature', label: 'Feature request' },
  { value: 'other', label: 'Something else' },
];

export default function ReportIssuePage() {
  const router = useRouter();
  const [user, setUser] = useState<User | null>(null);
  const [authChecked, setAuthChecked] = useState(false);

  const [issueType, setIssueType] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [urlOccurred, setUrlOccurred] = useState('');
  const [email, setEmail] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    const checkAuth = async () => {
      const supabase = createClient();
      const { data: { user: authedUser } } = await supabase.auth.getUser();
      if (!authedUser) {
        router.push('/login?redirect=/report-issue');
        return;
      }
      setUser(authedUser);
      setEmail(authedUser.email || '');
      setAuthChecked(true);
    };
    checkAuth();
  }, [router]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (description.length < 20) {
      setSubmitError('Description must be at least 20 characters.');
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await fetch('/api/report-issue', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ issueType, subject, description, url: urlOccurred }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit report');
      }

      setSubmitted(true);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Failed to submit report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show spinner while auth check is in progress
  if (!authChecked) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 text-white flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center py-12">
          <div className="w-16 h-16 rounded-full bg-green-900/40 border border-green-700
            flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-400" />
          </div>
          <h2 className="text-white text-xl font-medium mb-2">Report submitted</h2>
          <p className="text-gray-400 text-sm mb-6 max-w-sm mx-auto">
            Thank you for letting us know. We review all reports and will follow up
            at <span className="text-white">{user?.email}</span> if needed.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => {
                setSubmitted(false);
                setIssueType('');
                setSubject('');
                setDescription('');
                setUrlOccurred('');
                setSubmitError(null);
              }}
              className="px-4 py-2 rounded-lg border border-slate-600 text-gray-300
                hover:bg-slate-700 text-sm"
            >
              Submit another
            </button>
            <a
              href="/home"
              className="px-4 py-2 rounded-lg bg-gradient-to-r
                from-blue-600 to-purple-600 text-white text-sm font-medium"
            >
              Back to home
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 text-white">
      <PageHero
        title="Report an Issue"
        subtitle="Help us maintain a safe and professional community by reporting violations or technical issues."
        variant="strip"
        background="slate-blur"
        titleGradient="from-blue-300 to-indigo-400"
        eyebrow={
          <Link href="/home" className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors text-sm">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        }
      />

      {/* Main Content */}
      <section className="py-12">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto space-y-8">

            {/* Before You Report */}
            <Card className="bg-slate-800/50 backdrop-blur-sm border-slate-700/50">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <AlertTriangle className="w-5 h-5 mr-2 text-yellow-400" />
                  Before You Report
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3 text-sm text-slate-300">
                  <p>• <strong>Be specific:</strong> Provide detailed information about the issue</p>
                  <p>• <strong>Include evidence:</strong> Screenshots, links, or specific examples help us investigate</p>
                  <p>• <strong>Stay professional:</strong> We review all reports objectively</p>
                  <p>• <strong>Response time:</strong> We typically respond within 24–48 hours</p>
                </div>
              </CardContent>
            </Card>

            {/* Report Form */}
            <Card className="bg-slate-800/50 backdrop-blur-sm border-slate-700/50">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <MessageCircle className="w-5 h-5 mr-2 text-blue-400" />
                  Issue Report Form
                </CardTitle>
                <CardDescription className="text-slate-300">
                  Please provide as much detail as possible to help us address the issue effectively.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">

                  {/* Issue Type */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-200">Issue Type *</label>
                    <Select value={issueType} onValueChange={setIssueType}>
                      <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white">
                        <SelectValue placeholder="Select the type of issue" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-700 border-slate-600">
                        {ISSUE_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value} className="text-white hover:bg-slate-600">
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Subject */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-200">Subject * <span className="text-slate-400 font-normal">(max 100 chars)</span></label>
                    <Input
                      value={subject}
                      onChange={(e) => setSubject(e.target.value.slice(0, 100))}
                      placeholder="Brief summary of the issue"
                      className="bg-slate-700/50 border-slate-600 text-white placeholder-slate-400"
                      required
                    />
                    <p className="text-xs text-slate-500 text-right">{subject.length}/100</p>
                  </div>

                  {/* Description */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-200">
                      Description * <span className="text-slate-400 font-normal">(min 20, max 2000 chars)</span>
                    </label>
                    <Textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value.slice(0, 2000))}
                      placeholder="Please provide a detailed description including any relevant context, timestamps, usernames, or links..."
                      className="min-h-[120px] bg-slate-700/50 border-slate-600 text-white placeholder-slate-400"
                      spellCheck
                      required
                    />
                    <p className="text-xs text-slate-500 text-right">{description.length}/2000</p>
                  </div>

                  {/* URL where issue occurred */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-200">
                      URL where issue occurred <span className="text-slate-400 font-normal">(optional)</span>
                    </label>
                    <Input
                      value={urlOccurred}
                      onChange={(e) => setUrlOccurred(e.target.value)}
                      placeholder="https://indiestryvoice.com/..."
                      className="bg-slate-700/50 border-slate-600 text-white placeholder-slate-400"
                      type="url"
                    />
                  </div>

                  {/* Email (read-only, from account) */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-200">Your account email</label>
                    <Input
                      value={email}
                      readOnly
                      className="bg-slate-700/30 border-slate-600 text-slate-400 cursor-default"
                    />
                    <p className="text-xs text-slate-500">We&apos;ll follow up at this address if needed.</p>
                  </div>

                  {submitError && (
                    <p className="text-red-400 text-sm">{submitError}</p>
                  )}

                  {/* Actions */}
                  <div className="flex justify-end space-x-4 pt-6 border-t border-slate-700">
                    <Link href="/home">
                      <Button variant="outline" type="button">Cancel</Button>
                    </Link>
                    <Button
                      type="submit"
                      disabled={isSubmitting || !issueType || !subject || !description}
                      className="bg-blue-600 hover:bg-blue-500"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Submit Report
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Additional Resources */}
            <Card className="bg-slate-800/50 backdrop-blur-sm border-slate-700/50">
              <CardHeader>
                <CardTitle className="text-white">Additional Resources</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <UserIcon className="w-5 h-5 text-blue-400 mt-0.5" />
                    <div>
                      <h4 className="text-white font-medium">Community Guidelines</h4>
                      <p className="text-slate-300 text-sm mb-2">Review our community standards and acceptable use policies.</p>
                      <Link href="/community-guidelines" className="text-blue-400 hover:underline text-sm">View Guidelines →</Link>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Shield className="w-5 h-5 text-green-400 mt-0.5" />
                    <div>
                      <h4 className="text-white font-medium">Privacy Policy</h4>
                      <p className="text-slate-300 text-sm mb-2">Learn how we handle and protect your information.</p>
                      <Link href="/privacy-policy" className="text-blue-400 hover:underline text-sm">View Privacy Policy →</Link>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <MessageCircle className="w-5 h-5 text-purple-400 mt-0.5" />
                    <div>
                      <h4 className="text-white font-medium">Contact Support</h4>
                      <p className="text-slate-300 text-sm mb-2">Need general help or have questions? Contact our support team.</p>
                      <Link href="/contact" className="text-blue-400 hover:underline text-sm">Contact Support →</Link>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

          </div>
        </div>
      </section>
    </div>
  );
}
